import { useEffect, useMemo, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { getEtsyHealth } from '@/lib/etsy/health'
import { getEtsyShops } from '@/lib/etsy/shops'
import { getEtsyOrders } from '@/lib/etsy/orders'

function formatMoney(currency: string | null | undefined, amount: number | null | undefined): string {
  if (typeof amount !== 'number') return '—'
  return `${String(currency || 'USD')} ${amount.toFixed(2)}`
}

function formatCreated(createdAt: unknown): string {
  if (typeof createdAt === 'number') {
    const d = new Date(createdAt * 1000)
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 16).replace('T', ' ')
  }
  if (typeof createdAt === 'string' && createdAt) return createdAt
  return '—'
}

export default function EtsyOrdersList() {
  const { brandId } = useParams<{ brandId: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchQuery, setSearchQuery] = useState('')
  const [page, setPage] = useState(1)
  const [selectedShopId, setSelectedShopId] = useState<string>('')

  const base = brandId ? `/brands/${brandId}/integrations/etsy/orders` : '/integrations/etsy/orders'
  const backHref = brandId ? `/brands/${brandId}/settings` : '/brands/picker'

  const { data: health, isLoading: healthLoading } = useQuery({
    queryKey: ['etsy', 'health', brandId || 'legacy'],
    queryFn: () => getEtsyHealth(brandId),
    enabled: !!brandId,
  })
  const connected = !!brandId && health?.connected === true

  const { data: shopsData, isLoading: shopsLoading } = useQuery({
    queryKey: ['etsy', 'shops', brandId],
    queryFn: () => getEtsyShops(brandId),
    enabled: !!brandId && connected,
  })
  const shops = useMemo(() => shopsData?.shops ?? [], [shopsData?.shops])
  const shopIdFromUrl = searchParams.get('shopId')

  useEffect(() => {
    if (!shops.length) return
    if (shopIdFromUrl && shops.some((s) => String(s.shopId) === shopIdFromUrl)) {
      setSelectedShopId(shopIdFromUrl)
      return
    }
    const first = String(shops[0].shopId)
    setSelectedShopId(first)
    if (!shopIdFromUrl) setSearchParams({ shopId: first }, { replace: true })
  }, [shops, shopIdFromUrl, setSearchParams])

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['etsy', 'orders', brandId, selectedShopId, page],
    queryFn: () => getEtsyOrders(brandId!, selectedShopId, page, 25),
    enabled: !!brandId && connected && !!selectedShopId,
  })

  const filteredOrders = useMemo(() => {
    const orders = ordersData?.orders ?? []
    const q = searchQuery.trim().toLowerCase()
    if (!q) return orders
    return orders.filter((o) => {
      const id = String(o.receiptId || '').toLowerCase()
      const buyer = String(o.buyerName || '').toLowerCase()
      return id.includes(q) || buyer.includes(q)
    })
  }, [ordersData?.orders, searchQuery])

  const syncShopToUrl = (shopId: string) => setSearchParams(shopId ? { shopId } : {}, { replace: true })

  if (!brandId) {
    return (
      <div className="min-h-screen bg-gray-50 pb-24">
        <header className="sticky top-0 bg-white border-b border-gray-200 z-10 p-4">
          <h1 className="text-xl font-bold text-gray-900">Etsy orders</h1>
        </header>
        <main className="p-4">
          <p className="text-sm text-gray-600 mb-3">Choose a brand to view Etsy orders.</p>
          <Link to="/brands/picker" className="text-blue-600 font-medium underline">
            Pick a brand
          </Link>
        </main>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <header className="sticky top-0 bg-white border-b border-gray-200 z-10">
        <div className="p-4 flex items-start gap-2">
          <Link
            to={backHref}
            className="shrink-0 w-11 h-11 flex items-center justify-center rounded-lg text-sky-700 hover:bg-sky-50 text-xl"
            aria-label="Back"
          >
            ←
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Etsy orders
              {ordersData?.total != null ? (
                <span className="text-lg font-normal text-gray-600"> ({ordersData.total})</span>
              ) : null}
            </h1>
            <p className="text-xs text-gray-500 mt-1">Most recent first</p>
          </div>
        </div>
      </header>

      <main className="p-4 space-y-4">
        {healthLoading ? (
          <p className="text-sm text-gray-500">Checking connection…</p>
        ) : !connected ? (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm text-amber-950">
            <p className="font-medium mb-2">Etsy not connected</p>
            <p className="text-xs mb-3 opacity-90">Connect Etsy in Settings for this brand.</p>
            <Link
              to={`/brands/${brandId}/settings`}
              className="inline-block bg-amber-800 text-white px-4 py-2 rounded-lg font-medium touch-target-min-44 text-center"
            >
              Open Settings
            </Link>
          </div>
        ) : shopsLoading ? (
          <p className="text-sm text-gray-500">Loading shops…</p>
        ) : shops.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
            No Etsy shops found for this account.
          </div>
        ) : (
          <>
            <div>
              <label htmlFor="etsy-shop" className="block text-sm font-medium text-gray-700 mb-1">
                Etsy shop
              </label>
              <select
                id="etsy-shop"
                className="w-full border border-gray-300 rounded-lg px-3 py-3 text-base touch-target-min-44 bg-white"
                value={selectedShopId}
                onChange={(e) => {
                  const v = e.target.value
                  setSelectedShopId(v)
                  setPage(1)
                  syncShopToUrl(v)
                }}
              >
                {shops.map((s) => (
                  <option key={String(s.shopId)} value={String(s.shopId)}>
                    {s.shopName || `Shop ${String(s.shopId)}`} ({String(s.shopId)})
                  </option>
                ))}
              </select>
            </div>

            <input
              type="search"
              placeholder="Search orders…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg text-base min-h-[48px]"
              aria-label="Search orders"
            />

            {ordersLoading ? (
              <p className="text-sm text-gray-500">Loading orders…</p>
            ) : (
              <>
                {filteredOrders.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                    <p className="text-gray-600">
                      {searchQuery.trim() ? 'No orders match your search.' : 'No orders found for this shop.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredOrders.map((o) => (
                      <Link
                        key={String(o.receiptId)}
                        to={`${base}/${selectedShopId}/${String(o.receiptId)}`}
                        className="block bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900">Receipt {String(o.receiptId)}</div>
                            <div className="text-xs text-gray-600 mt-1">
                              Buyer: {o.buyerName || '—'} • {formatCreated(o.createdAt)}
                            </div>
                          </div>
                          <span className="text-[11px] font-semibold uppercase tracking-wide rounded-full px-2 py-1 bg-gray-100 text-gray-700">
                            {String(o.status || 'unknown')}
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 mt-3 text-xs text-gray-700">
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Total</div>
                            <div className="font-mono">{formatMoney(o.currency, o.total)}</div>
                          </div>
                          <div>
                            <div className="text-[10px] text-gray-500 uppercase tracking-wide">Items</div>
                            <div className="font-mono">{o.itemCount ?? '—'}</div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}

                {!searchQuery.trim() && ordersData && ordersData.lastPage > 1 && (
                  <div className="flex justify-between items-center pt-2">
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((x) => Math.max(1, x - 1))}
                      className="px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-40 touch-target-min-44"
                    >
                      Previous
                    </button>
                    <span className="text-sm text-gray-600">
                      Page {ordersData.currentPage} of {ordersData.lastPage}
                    </span>
                    <button
                      type="button"
                      disabled={page >= ordersData.lastPage}
                      onClick={() => setPage((x) => x + 1)}
                      className="px-4 py-3 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-40 touch-target-min-44"
                    >
                      Next
                    </button>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  )
}

